import { getConfig } from '../config';
import { apiPostForm } from '../api/client';
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  savePKCESession,
  peekPKCESession,
  clearPKCESession,
} from './pkce';

interface TokenResponse {
  id_token?: string;
  token_type: string;
  expires_in: number;
}

/** Один обмен кода на токены на authorization code (React Strict Mode монтирует callback дважды). */
const inFlightAuthCodeExchange = new Map<string, Promise<string>>();

/**
 * Инициирует OAuth flow: генерирует PKCE, сохраняет в sessionStorage,
 * делает редирект на authorize endpoint istock.info.
 */
export async function initiateLogin(returnUrl: string = '/'): Promise<void> {
  const config = getConfig();

  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();

  savePKCESession(verifier, state, returnUrl);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.oauthClientID,
    redirect_uri: config.oauthRedirectURI,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    scope: 'openid profile company company:admin',
  });

  // Прямой редирект на authorize API. Кука access_token с SameSite=Lax отправляется
  // при top-level навигации localhost→istock, bounce не нужен.
  const redirectUrl = `${config.oauthIssuerURL}/api/v2/identity/oauth/authorize?${params.toString()}`;
  window.location.assign(redirectUrl);
}

/**
 * Обрабатывает OAuth callback: проверяет state, обменивает code на токены через бэкенд.
 * Возвращает return URL для редиректа после успешной авторизации.
 *
 * Бросает ошибку при несовпадении state или ошибке обмена кода.
 */
export function handleCallback(code: string, incomingState: string): Promise<string> {
  const existing = inFlightAuthCodeExchange.get(code);
  if (existing) {
    return existing;
  }

  const session = peekPKCESession();

  if (!session) {
    return Promise.reject(
      new Error('OAuth session not found. Please try logging in again.'),
    );
  }

  if (session.state !== incomingState) {
    clearPKCESession();
    return Promise.reject(new Error('OAuth state mismatch. Possible CSRF attack.'));
  }

  const promise = exchangeAuthorizationCode(code, session).finally(() => {
    inFlightAuthCodeExchange.delete(code);
  });
  inFlightAuthCodeExchange.set(code, promise);
  return promise;
}

async function exchangeAuthorizationCode(
  code: string,
  session: { verifier: string; state: string; returnUrl: string },
): Promise<string> {
  const config = getConfig();

  try {
    await apiPostForm<TokenResponse>('/api/oauth/token', {
      grant_type: 'authorization_code',
      code,
      code_verifier: session.verifier,
      redirect_uri: config.oauthRedirectURI,
    });
    clearPKCESession();
    return session.returnUrl;
  } catch (err) {
    clearPKCESession();
    throw err;
  }
}
